import { forwardRef } from 'react';
import { NavLink } from 'react-router-dom';
import { Icon } from './Icon';
import { useAuth } from '../hooks/useAuth';
import { LOGOUT_URL } from '../lib/auth';

const NAV = [
  { to: '/dashboard', label: 'Executive Dashboard', icon: 'dashboard' },
  { to: '/transactions', label: 'Transactions', icon: 'txns' },
  { to: '/invoices', label: 'Invoices · Receivables', icon: 'invoice' },
  { to: '/clients', label: 'Clients', icon: 'clients' },
  { to: '/accuracy', label: 'Forecast Accuracy', icon: 'accuracy' },
  { to: '/alerts', label: 'Alerts · Settings', icon: 'alerts' },
] as const;

function initials(email: string): string {
  const name = email.split('@')[0] ?? '';
  const parts = name.split(/[.\-_]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return (name.slice(0, 2) || '··').toUpperCase();
}

export interface SidebarProps {
  /** Drawer state. Only meaningful below the 900px nav breakpoint. */
  open: boolean;
  /** Called when a nav target is chosen, so the drawer can close behind it. */
  onNavigate: () => void;
}

export const Sidebar = forwardRef<HTMLElement, SidebarProps>(function Sidebar(
  { open, onNavigate },
  ref,
) {
  const { user } = useAuth();

  return (
    <aside
      ref={ref}
      id="app-nav"
      className={'sidebar' + (open ? ' open' : '')}
      tabIndex={-1}
      aria-label="Main navigation"
    >
      <div className="brand">
        <img src="/brand/fortalice-wordmark.svg" alt="Fortalice" className="brand-wordmark" />
        <div className="brand-sub">Cash Forecasting</div>
      </div>

      <div className="nav-section">Operations</div>
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onNavigate}
          className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}
        >
          <Icon name={item.icon} className="nav-icon" />
          <span>{item.label}</span>
        </NavLink>
      ))}

      <div className="sidebar-footer">
        <div className="user-chip">
          <div className="avatar">{initials(user?.email ?? '')}</div>
          <div className="user-chip-text">
            <strong>{user?.email ?? 'Signed in'}</strong>
            <span>{user?.isEditor ? 'Editor' : 'Viewer'}</span>
          </div>
        </div>
        <NavLink
          to="/settings"
          onClick={onNavigate}
          className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}
        >
          <Icon name="settings" className="nav-icon" />
          <span>Settings</span>
        </NavLink>
        <a className="nav-item" href={LOGOUT_URL}>
          <Icon name="logout" className="nav-icon" />
          <span>Sign out</span>
        </a>
      </div>
    </aside>
  );
});
